import type { Contract } from "@cva/shared";
import { createORPCClient } from "@orpc/client";
import { RPCLink } from "@orpc/client/fetch";
import type { ContractRouterClient } from "@orpc/contract";
import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import { QueryClient } from "@tanstack/react-query";
import { noteRateLimit, retryAfterMs } from "./rate-limit";

/** Buckets that refuse every call of this person (not only one action): pause them all. */
const GLOBAL_SCOPES = new Set(["api", "public"]);

/**
 * While the per-user limit refuses anything, calls are answered here with the
 * same 429 instead of going out: polling and retries do not hammer the server.
 */
let blocked: { until: number; body: string } | null = null;

async function rpcFetch(request: Request, init: RequestInit): Promise<Response> {
  if (blocked && Date.now() < blocked.until) {
    return new Response(blocked.body, {
      status: 429,
      headers: { "Content-Type": "application/json" },
    });
  }
  const response = await fetch(request, init);
  if (response.status !== 429) return response;
  const body = await response.clone().text();
  let error: { message?: string; data?: { scope?: string } } | undefined;
  try {
    error = (JSON.parse(body) as { json?: typeof error }).json;
  } catch {
    // not an oRPC error (proxy): treat like the general limit
  }
  const scope = error?.data?.scope;
  // Limits of one action (exports, cut-outs …) are shown by the caller itself.
  if (!scope || GLOBAL_SCOPES.has(scope)) {
    const wait = retryAfterMs(response);
    blocked = { until: Date.now() + wait, body };
    noteRateLimit(error?.message, wait);
  }
  return response;
}

const link = new RPCLink({
  url: `${window.location.origin}/api/rpc`,
  fetch: (request, init) => rpcFetch(request, init),
});

export const client: ContractRouterClient<Contract> = createORPCClient(link);
export const orpc = createTanstackQueryUtils(client);

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: (count, error) => {
        const status = (error as { status?: number }).status;
        if (status && status >= 400 && status < 500) return false;
        return count < 2;
      },
    },
  },
});
